import { computed, onUnmounted, ref, watch } from 'vue';
import {
  CONTROL_ROOM_REMINDER_THRESHOLDS,
  clearControlRoomReminderStartedAt,
  getControlRoomReminderElapsedSeconds,
  getControlRoomReminderMutedMessageId,
  getLatestIncomingMessageId,
  isControlRoomReminderMuted,
  loadControlRoomReminderStartedAt,
  setControlRoomReminderMutedMessageId,
  shouldShowControlRoomReminder,
} from 'dashboard/helper/controlRoomReminderHelper';

export function useControlRoomReminder({
  conversationId,
  messages,
  enabled,
  hasBaseUrl,
  hasPhoneNumber,
}) {
  const startedAt = ref(Date.now());
  const now = ref(Date.now());
  const mutedMessageId = ref(null);
  let timer = null;

  const clearTimer = () => {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  };

  const latestIncomingMessageId = computed(() =>
    getLatestIncomingMessageId(messages.value)
  );

  const elapsedSeconds = computed(() =>
    getControlRoomReminderElapsedSeconds({
      startedAt: startedAt.value,
      now: now.value,
    })
  );

  const isMuted = computed(() =>
    isControlRoomReminderMuted({
      mutedMessageId: mutedMessageId.value,
      latestIncomingMessageId: latestIncomingMessageId.value,
    })
  );

  const showReminder = computed(() =>
    shouldShowControlRoomReminder({
      enabled: enabled.value,
      hasBaseUrl: hasBaseUrl.value,
      isMuted: isMuted.value,
    })
  );

  const startTimer = () => {
    clearTimer();
    timer = window.setInterval(() => {
      if (elapsedSeconds.value >= CONTROL_ROOM_REMINDER_THRESHOLDS.CRITICAL) {
        clearTimer();
        return;
      }

      now.value = Date.now();
    }, 1000);
  };

  const reset = () => {
    if (!enabled.value || !hasBaseUrl.value || !conversationId.value) {
      mutedMessageId.value = null;
      clearTimer();
      return;
    }

    mutedMessageId.value = getControlRoomReminderMutedMessageId(
      conversationId.value
    );
    startedAt.value = loadControlRoomReminderStartedAt(conversationId.value);
    now.value = Date.now();

    if (isMuted.value) {
      clearTimer();
      return;
    }

    startTimer();
  };

  const mute = () => {
    const messageId = latestIncomingMessageId.value;
    if (!messageId || !conversationId.value) {
      return;
    }

    setControlRoomReminderMutedMessageId(conversationId.value, messageId);
    mutedMessageId.value = messageId;
    clearControlRoomReminderStartedAt(conversationId.value);
    clearTimer();
  };

  const onOpen = () => {
    if (!hasPhoneNumber.value || !conversationId.value) {
      return;
    }

    mute();
  };

  watch(
    conversationId,
    (newId, oldId) => {
      if (!newId) {
        return;
      }

      if (oldId === undefined || newId !== oldId) {
        reset();
      }
    },
    { immediate: true }
  );

  watch(latestIncomingMessageId, (newId, oldId) => {
    if (!newId || newId === oldId || isMuted.value) {
      return;
    }

    if (!enabled.value || !hasBaseUrl.value || !conversationId.value) {
      return;
    }

    startedAt.value = loadControlRoomReminderStartedAt(conversationId.value);
    now.value = Date.now();
    startTimer();
  });

  onUnmounted(() => {
    clearTimer();
  });

  return {
    showReminder,
    elapsedSeconds,
    isMuted,
    reset,
    mute,
    onOpen,
  };
}
